import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { EstadoTag, fechaHora, poli, qs } from './poliApi';

/**
 * P-10 Financiero. Financiero registra el valor por tipo de prueba y/o por compania y Gerencia lo
 * aprueba (quien lo registra no puede aprobarlo). La consulta muestra si la prueba existe, su estado
 * y el valor que le corresponde; el servidor no envia el informe a este perfil.
 */
const z = (n) => String(n).padStart(2, '0');
const dia = (d) => `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
const hoy = new Date();
const usd = (v) => (v == null ? '' : Number(v).toLocaleString('es-EC', { style: 'currency', currency: 'USD' }));
const ESTADO_TARIFA = {
    PENDIENTE: { t: 'Pendiente de Gerencia', c: '#b26a00' },
    APROBADA: { t: 'Aprobada', c: '#2e7d32' },
    RECHAZADA: { t: 'Rechazada', c: '#c62828' },
    REEMPLAZADA: { t: 'Reemplazada', c: '#78909c' }
};
const campo = { display: 'flex', flexDirection: 'column', gap: 4 };
const num = { fontVariantNumeric: 'tabular-nums', textAlign: 'right' };

const Cifra = ({ titulo, valor, nota, alerta }) => (
    <div className="col-6 md:col-3">
        <div className="card mb-0 h-full" style={alerta ? { borderLeft: '4px solid #b26a00' } : undefined}>
            <div className="text-600 text-sm">{titulo}</div>
            <div className="text-2xl font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</div>
            {nota && <div className="text-500 text-xs mt-1">{nota}</div>}
        </div>
    </div>
);

const PoliFinanciero = () => {
    const toast = useRef(null);
    const [cfg, setCfg] = useState(null);
    const [f, setF] = useState({ desde: dia(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), hasta: dia(hoy), companiaId: null, proyectoId: null, buscar: '' });
    const [datos, setDatos] = useState(null);
    const [tarifas, setTarifas] = useState([]);
    const [obs, setObs] = useState([]);
    const [nueva, setNueva] = useState(null);
    const [rechazo, setRechazo] = useState(null);
    const [observar, setObservar] = useState(null);
    const [cargando, setCargando] = useState(false);

    const aviso = (severity, detail) => toast.current && toast.current.show({ severity, summary: severity === 'error' ? 'Error' : 'Listo', detail, life: 4000 });
    const esFin = cfg && cfg.perfiles.includes('FINANCIERO');
    const esGer = cfg && cfg.perfiles.includes('GERENCIA');

    const cargarTodo = useCallback(async () => {
        setCargando(true);
        try {
            const [t, o] = await Promise.all([poli('/financiero/tarifas'), poli('/financiero/observaciones')]);
            setTarifas(t);
            setObs(o);
        } catch (e) {
            aviso('error', e.message);
        } finally {
            setCargando(false);
        }
    }, []);

    const consultar = useCallback(async () => {
        try {
            setDatos(await poli(`/financiero/pruebas?${qs(f)}`));
        } catch (e) {
            aviso('error', e.message);
        }
    }, [f]);

    useEffect(() => { poli('/config').then(setCfg).catch((e) => aviso('error', e.message)); cargarTodo(); }, [cargarTodo]);
    // la busqueda por texto se lanza con el boton o Enter, no en cada tecla
    useEffect(() => { consultar(); }, [f.desde, f.hasta, f.companiaId, f.proyectoId]); // eslint-disable-line react-hooks/exhaustive-deps

    const accion = async (fn, ok) => {
        try {
            await fn();
            aviso('success', ok);
            cargarTodo();
            consultar();
        } catch (e) {
            aviso('error', e.message);
        }
    };

    const companias = (cfg && cfg.companias) || [];
    const proyectos = (companias.find((c) => c.compania_id === f.companiaId) || {}).proyectos || [];
    const tipos = ((cfg && cfg.catalogos) || []).filter((c) => c.tipo === 'TIPO_PRUEBA' && c.activo);
    const t = (datos && datos.totales) || {};
    const pendientesTarifa = tarifas.filter((x) => x.estado === 'PENDIENTE').length;
    const pendientesObs = obs.filter((x) => !x.revisada_por).length;

    const estadoTarifa = (r) => {
        const e = ESTADO_TARIFA[r.estado] || { t: r.estado, c: '#555' };
        return <span style={{ color: e.c, fontWeight: 600 }}>{e.t}</span>;
    };

    return (
        <div className="grid">
            <Toast ref={toast} />
            <div className="col-12 flex align-items-center justify-content-between flex-wrap gap-2">
                <h4 className="m-0">Financiero de poligrafia</h4>
                <span className="text-600 text-sm"><i className="pi pi-lock mr-1" />Sin acceso al contenido de los informes</span>
            </div>
            <div className="col-12">
                <TabView>
                    <TabPanel header="Pruebas y valores">
                        <div className="flex flex-wrap align-items-end gap-3 mb-3">
                            <label style={campo} htmlFor="fin-desde">Desde
                                <input id="fin-desde" type="date" className="p-inputtext" value={f.desde} onChange={(e) => setF({ ...f, desde: e.target.value })} />
                            </label>
                            <label style={campo} htmlFor="fin-hasta">Hasta
                                <input id="fin-hasta" type="date" className="p-inputtext" value={f.hasta} onChange={(e) => setF({ ...f, hasta: e.target.value })} />
                            </label>
                            <label style={campo} htmlFor="fin-compania">Compania
                                <Dropdown inputId="fin-compania" value={f.companiaId} options={companias} optionLabel="nombre" optionValue="compania_id"
                                    showClear placeholder="Todas" onChange={(e) => setF({ ...f, companiaId: e.value, proyectoId: null })} style={{ minWidth: '12rem' }} />
                            </label>
                            <label style={campo} htmlFor="fin-proyecto">Proyecto
                                <Dropdown inputId="fin-proyecto" value={f.proyectoId} options={proyectos} optionLabel="nombre" optionValue="proyecto_id"
                                    showClear placeholder="Todos" disabled={!f.companiaId} onChange={(e) => setF({ ...f, proyectoId: e.value })} style={{ minWidth: '10rem' }} />
                            </label>
                            <label style={campo} htmlFor="fin-buscar">Cedula, codigo o nombre
                                <InputText id="fin-buscar" value={f.buscar} onChange={(e) => setF({ ...f, buscar: e.target.value })}
                                    onKeyDown={(e) => e.key === 'Enter' && consultar()} />
                            </label>
                            <Button label="Consultar" icon="pi pi-search" onClick={consultar} />
                        </div>
                        {datos && (
                            <>
                                <div className="grid">
                                    <Cifra titulo="Pruebas en el rango" valor={t.total} />
                                    <Cifra titulo="Realizadas" valor={t.realizadas} nota="estados 5 a 8" />
                                    <Cifra titulo="Valor de las realizadas" valor={usd(t.valor_realizadas)} nota="con la tarifa aprobada vigente" />
                                    <Cifra titulo="Realizadas sin tarifa aprobada" valor={t.realizadas_sin_tarifa} alerta={t.realizadas_sin_tarifa > 0}
                                        nota={t.realizadas_sin_tarifa > 0 ? 'falta registrar o aprobar su tarifa' : undefined} />
                                </div>
                                <h5 className="mt-3">Por compania y proyecto</h5>
                                <DataTable value={datos.porCompania} size="small" emptyMessage="Sin pruebas en el rango" responsiveLayout="scroll">
                                    <Column header="Compania" field="compania" />
                                    <Column header="Proyecto" field="proyecto" />
                                    <Column header="Pruebas" field="total" style={num} />
                                    <Column header="Realizadas" field="realizadas" style={num} />
                                    <Column header="Sin tarifa" field="realizadas_sin_tarifa" style={num} />
                                    <Column header="Valor realizadas" body={(r) => usd(r.valor_realizadas)} style={num} />
                                </DataTable>
                                <h5 className="mt-4">Pruebas <span className="text-600">({datos.pruebas.length})</span></h5>
                                <DataTable value={datos.pruebas} size="small" dataKey="prueba_id" paginator rows={15} emptyMessage="Sin pruebas" responsiveLayout="scroll">
                                    <Column header="Codigo" field="codigo" bodyStyle={{ whiteSpace: 'nowrap' }} />
                                    <Column header="Colaborador" body={(r) => <div>{r.apellidos} {r.nombres}<br /><small className="text-600">{r.cedula}</small></div>} />
                                    <Column header="Compania" body={(r) => <div>{r.compania}<br /><small className="text-600">{r.proyecto}</small></div>} />
                                    <Column header="Tipo" field="tipo" />
                                    <Column header="Fecha" field="fecha" bodyStyle={{ whiteSpace: 'nowrap' }} />
                                    <Column header="Estado" body={(r) => <EstadoTag fila={r} />} />
                                    <Column header="Valor" body={(r) => (r.monto != null ? <div className={r.realizada ? undefined : 'text-500'}
                                        title={r.realizada ? undefined : 'No suma al total: la prueba no esta realizada'}>{usd(r.monto)}<br />
                                        <small className="text-600">{r.realizada ? r.origen_tarifa : 'referencial'}</small></div>
                                        : <small style={{ color: '#b26a00' }}>Sin tarifa aprobada</small>)} style={num} />
                                    <Column header="" body={(r) => (
                                        <div className="flex gap-2 align-items-center justify-content-end">
                                            {r.observaciones_pendientes > 0 && <span className="text-sm" style={{ color: '#b26a00' }} title="Observaciones sin revisar">
                                                <i className="pi pi-comment mr-1" />{r.observaciones_pendientes}</span>}
                                            {esGer && <Button icon="pi pi-comment" className="p-button-text p-button-sm" title="Observacion a Financiero"
                                                aria-label={`Observacion a Financiero sobre ${r.codigo}`} onClick={() => setObservar({ prueba: r, texto: '' })} />}
                                        </div>
                                    )} />
                                </DataTable>
                            </>
                        )}
                    </TabPanel>

                    <TabPanel header={`Tarifas${pendientesTarifa ? ` (${pendientesTarifa} pendientes)` : ''}`}>
                        <div className="flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                            <p className="m-0 text-600 text-sm" style={{ maxWidth: '46rem' }}>
                                A cada prueba se le aplica la tarifa aprobada mas especifica vigente a su fecha: primero la de su compania y tipo,
                                luego la de su compania, y por ultimo la general por tipo. Lo que se aprueba es la tarifa, no la prueba.
                            </p>
                            {esFin && <Button label="Registrar tarifa" icon="pi pi-plus"
                                onClick={() => setNueva({ tipoPruebaId: null, companiaId: null, monto: '', vigenteDesde: dia(hoy), nota: '' })} />}
                        </div>
                        <DataTable value={tarifas} size="small" dataKey="tarifa_id" emptyMessage="Todavia no hay tarifas" responsiveLayout="scroll" loading={cargando}>
                            <Column header="Compania" body={(r) => r.compania || <span className="text-600">General</span>} />
                            <Column header="Tipo de prueba" body={(r) => r.tipo || <span className="text-600">Cualquiera</span>} />
                            <Column header="Valor" body={(r) => usd(r.monto)} style={num} />
                            <Column header="Vigente desde" field="vigente_desde" />
                            <Column header="Estado" body={estadoTarifa} />
                            <Column header="Registro" body={(r) => <div>{r.registrado_por}<br /><small className="text-600">{fechaHora(r.fecha_crea)}</small>{r.nota && <><br /><small>{r.nota}</small></>}</div>} />
                            <Column header="Revision" body={(r) => (r.revisado_por ? <div>{r.revisado_por}<br /><small className="text-600">{fechaHora(r.fecha_revision)}</small>
                                {r.motivo_rechazo && <><br /><small style={{ color: '#c62828' }}>{r.motivo_rechazo}</small></>}</div> : '')} />
                            {esGer && <Column header="" body={(r) => (r.estado === 'PENDIENTE' ? (r.propia ? <small className="text-600">La registro usted: la aprueba otra persona</small> : (
                                <div className="flex gap-2">
                                    <Button label="Aprobar" icon="pi pi-check" className="p-button-sm p-button-success"
                                        onClick={() => accion(() => poli(`/financiero/tarifas/${r.tarifa_id}/aprobar`, 'POST', {}), 'Tarifa aprobada')} />
                                    <Button label="Rechazar" icon="pi pi-times" className="p-button-sm p-button-outlined p-button-danger"
                                        onClick={() => setRechazo({ tarifa: r, motivo: '' })} />
                                </div>)) : '')} />}
                        </DataTable>
                    </TabPanel>

                    <TabPanel header={`Observaciones${pendientesObs ? ` (${pendientesObs} sin revisar)` : ''}`}>
                        <DataTable value={obs} size="small" dataKey="observacion_id" emptyMessage="Gerencia no ha dejado observaciones" responsiveLayout="scroll">
                            <Column header="Prueba" body={(r) => <div>{r.codigo}<br /><small className="text-600">{r.compania}</small></div>} />
                            <Column header="Observacion" body={(r) => <div style={{ whiteSpace: 'pre-wrap', maxWidth: '36rem' }}>{r.texto}</div>} />
                            <Column header="De" body={(r) => <div>{r.autor}<br /><small className="text-600">{fechaHora(r.fecha)}</small></div>} />
                            <Column header="Estado" body={(r) => (r.revisada_por
                                ? <div><span style={{ color: '#2e7d32', fontWeight: 600 }}>Revisada</span><br /><small className="text-600">{r.revisada_por} · {fechaHora(r.fecha_revisada)}</small></div>
                                : esFin ? <Button label="Marcar revisada" icon="pi pi-check" className="p-button-sm p-button-outlined"
                                    onClick={() => accion(() => poli(`/financiero/observaciones/${r.observacion_id}/revisada`, 'POST', {}), 'Observacion revisada')} />
                                    : <span style={{ color: '#b26a00', fontWeight: 600 }}>Pendiente de revision</span>)} />
                        </DataTable>
                    </TabPanel>
                </TabView>
            </div>

            <Dialog header="Registrar tarifa" visible={!!nueva} style={{ width: 'min(30rem, 96vw)' }} onHide={() => setNueva(null)}>
                {nueva && (
                    <div className="flex flex-column gap-3">
                        <label style={campo} htmlFor="tar-compania">Compania (vacio = tarifa general)
                            <Dropdown inputId="tar-compania" value={nueva.companiaId} options={companias} optionLabel="nombre" optionValue="compania_id"
                                showClear placeholder="General" onChange={(e) => setNueva({ ...nueva, companiaId: e.value })} />
                        </label>
                        <label style={campo} htmlFor="tar-tipo">Tipo de prueba (vacio = cualquiera)
                            <Dropdown inputId="tar-tipo" value={nueva.tipoPruebaId} options={tipos} optionLabel="nombre" optionValue="catalogo_id"
                                showClear placeholder="Cualquiera" onChange={(e) => setNueva({ ...nueva, tipoPruebaId: e.value })} />
                        </label>
                        <label style={campo} htmlFor="tar-monto">Valor en USD
                            <InputText id="tar-monto" inputMode="decimal" value={nueva.monto} placeholder="85,00" onChange={(e) => setNueva({ ...nueva, monto: e.target.value })} />
                        </label>
                        <label style={campo} htmlFor="tar-desde">Vigente desde
                            <input id="tar-desde" type="date" className="p-inputtext" value={nueva.vigenteDesde} onChange={(e) => setNueva({ ...nueva, vigenteDesde: e.target.value })} />
                        </label>
                        <label style={campo} htmlFor="tar-nota">Nota (opcional)
                            <InputTextarea id="tar-nota" rows={2} value={nueva.nota} maxLength={500} onChange={(e) => setNueva({ ...nueva, nota: e.target.value })} />
                        </label>
                        {!nueva.companiaId && !nueva.tipoPruebaId && <small style={{ color: '#c62828' }}>Indique la compania, el tipo de prueba o ambos.</small>}
                        <div className="flex justify-content-end gap-2">
                            <Button label="Cancelar" className="p-button-text" onClick={() => setNueva(null)} />
                            <Button label="Registrar para aprobacion" icon="pi pi-send" disabled={(!nueva.companiaId && !nueva.tipoPruebaId) || !nueva.monto}
                                onClick={() => accion(async () => { await poli('/financiero/tarifas', 'POST', nueva); setNueva(null); }, 'Tarifa registrada: queda pendiente de Gerencia')} />
                        </div>
                    </div>
                )}
            </Dialog>

            <Dialog header="Rechazar tarifa" visible={!!rechazo} style={{ width: 'min(28rem, 96vw)' }} onHide={() => setRechazo(null)}>
                {rechazo && (
                    <div className="flex flex-column gap-3">
                        <div>{rechazo.tarifa.compania || 'General'} · {rechazo.tarifa.tipo || 'Cualquier tipo'} · {usd(rechazo.tarifa.monto)}</div>
                        <label style={campo} htmlFor="rec-motivo">Motivo del rechazo
                            <InputTextarea id="rec-motivo" rows={3} value={rechazo.motivo} maxLength={500} onChange={(e) => setRechazo({ ...rechazo, motivo: e.target.value })} />
                        </label>
                        <div className="flex justify-content-end gap-2">
                            <Button label="Cancelar" className="p-button-text" onClick={() => setRechazo(null)} />
                            <Button label="Rechazar" icon="pi pi-times" className="p-button-danger" disabled={!rechazo.motivo.trim()}
                                onClick={() => accion(async () => { await poli(`/financiero/tarifas/${rechazo.tarifa.tarifa_id}/rechazar`, 'POST', { motivo: rechazo.motivo }); setRechazo(null); }, 'Tarifa rechazada')} />
                        </div>
                    </div>
                )}
            </Dialog>

            <Dialog header="Observacion a Financiero" visible={!!observar} style={{ width: 'min(28rem, 96vw)' }} onHide={() => setObservar(null)}>
                {observar && (
                    <div className="flex flex-column gap-3">
                        <div>Prueba <b>{observar.prueba.codigo}</b> · {observar.prueba.compania}</div>
                        <label style={campo} htmlFor="obs-texto">Observacion
                            <InputTextarea id="obs-texto" rows={4} value={observar.texto} maxLength={2000} onChange={(e) => setObservar({ ...observar, texto: e.target.value })} />
                        </label>
                        <div className="flex justify-content-end gap-2">
                            <Button label="Cancelar" className="p-button-text" onClick={() => setObservar(null)} />
                            <Button label="Enviar a Financiero" icon="pi pi-send" disabled={!observar.texto.trim()}
                                onClick={() => accion(async () => { await poli(`/pruebas/${observar.prueba.prueba_id}/observaciones-financiero`, 'POST', { texto: observar.texto }); setObservar(null); }, 'Observacion enviada a Financiero')} />
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default PoliFinanciero;
